import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Node Store
 *
 * IMPORTANT: This store is for LOCAL DEVELOPMENT ONLY.
 * The `nodes` array contains localhost URLs for a local testnet/development environment.
 *
 * In production, this should be replaced with:
 * - Dynamic node discovery from the network
 * - Configuration from environment variables
 * - Node registry/bootstrap nodes
 *
 * The hardcoded nodes below are NOT production data - they are development placeholders.
 */
export const useNodeStore = defineStore('node', () => {
  // State
  const connectedNode = ref<string | null>(null)
  const selectedNodeIndex = ref(0)
  const authToken = ref<string>('test-token')
  const nodeStatus = ref<'disconnected' | 'connecting' | 'connected'>('disconnected')

  // Development-only node configuration
  // These are localhost URLs for local testnet nodes exposed via Docker ports
  // NOT FOR PRODUCTION USE
  const nodes = [
    'http://localhost:8081',
    'http://localhost:8082',
    'http://localhost:8083',
    'http://localhost:8084',
    'http://localhost:8085',
    'http://localhost:8086'
  ]

  // Computed
  const apiUrl = computed(() => {
    return connectedNode.value || nodes[selectedNodeIndex.value]
  })

  const isConnected = computed(() => nodeStatus.value === 'connected')

  // Actions
  const connect = async (nodeUrl?: string) => {
    nodeStatus.value = 'connecting'

    try {
      const targetUrl = nodeUrl || nodes[selectedNodeIndex.value]

      // Test connection
      const response = await fetch(`${targetUrl}/health`)
      if (response.ok) {
        connectedNode.value = targetUrl
        nodeStatus.value = 'connected'
        console.log(`Connected to node: ${targetUrl}`)
        return true
      } else {
        throw new Error('Node health check failed')
      }
    } catch (error) {
      console.error('Failed to connect to node:', error)
      nodeStatus.value = 'disconnected'
      connectedNode.value = null
      return false
    }
  }

  const disconnect = () => {
    connectedNode.value = null
    nodeStatus.value = 'disconnected'
  }

  const switchNode = async (index: number) => {
    if (index >= 0 && index < nodes.length) {
      selectedNodeIndex.value = index
      await connect(nodes[index])
    }
  }

  // Initialize with first node
  const initialize = async () => {
    await connect()
  }

  return {
    // State
    connectedNode,
    selectedNodeIndex,
    authToken,
    nodeStatus,
    nodes,

    // Computed
    apiUrl,
    isConnected,

    // Actions
    connect,
    disconnect,
    switchNode,
    initialize
  }
})